/**
 * Signaux d'anomalie de la timeline support (C5).
 *
 * Ce sont les constats de l'enquête ASL JONCOURS que personne n'avait vus
 * sans SQL : des invitations ajoutées mais jamais envoyées, des votes émis
 * depuis les appareils du créateur sous plusieurs noms, une session IA dont il
 * ne restait que la télémétrie. Ce sont des **pistes** pour le support, pas des
 * verdicts — d'où des formulations prudentes.
 *
 * Fonction pure, dans `core` pour être couverte par la suite de tests de la
 * CI (seule `test:unit:fast` tourne).
 */
import type { AdminTimelineEntry } from "../../services/PulseMetricsService";

const str = (value: unknown): string | null =>
  typeof value === "string" && value.length > 0 ? value : null;

export function detectAnomalies(entries: AdminTimelineEntry[]): string[] {
  const out: string[] = [];

  const neverSent = entries.filter(
    (e) => e.source === "invitation" && e.kind === "added_not_sent",
  ).length;
  if (neverSent > 0) {
    const s = neverSent > 1 ? "s" : "";
    out.push(`${neverSent} invitation${s} ajoutée${s} mais jamais envoyée${s}`);
  }

  // Votes venus des propres appareils du créateur (`ownVote`, calculé côté
  // serveur : compte ou empreinte rattachée) sur un même sondage, sous
  // plusieurs noms — le cas « Stéphane Sans » d'ASL JONCOURS.
  //
  // Version précédente retirée (recette 2026-09-16) : elle comparait les votes
  // reçus aux votes émis par empreinte et IP. Or le vote du créateur sur son
  // propre sondage figure dans les deux listes → il se désignait lui-même
  // (faux positif). Et l'IP n'est jamais stockée (0 vote sur 727 en 30 jours),
  // donc la branche IP ne pouvait rien détecter.
  const ownNamesByPoll = new Map<string, Set<string>>();
  for (const e of entries) {
    if (e.source !== "vote_received" || e.detail?.ownVote !== true) continue;
    const key = e.poll_id ?? "";
    const names = ownNamesByPoll.get(key) ?? new Set<string>();
    names.add(e.summary);
    ownNamesByPoll.set(key, names);
  }
  for (const [, names] of ownNamesByPoll) {
    if (names.size >= 2) {
      out.push(
        `${names.size} votes depuis ses propres appareils sur un même sondage, sous des noms différents : ${[...names].join(", ")}`,
      );
    }
  }

  const aiMessages = entries.filter(
    (e) => e.source === "ai" && e.kind === "user",
  ).length;
  if (aiMessages === 0 && entries.some((e) => e.source === "ai_event")) {
    out.push(
      "Télémétrie IA présente mais aucune conversation stockée (session antérieure à la persistance invitée)",
    );
  }

  const modifications = entries.filter(
    (e) => e.source === "ai_event" && e.kind === "poll_modification_ai",
  ).length;
  if (modifications >= 3) {
    out.push(`Plafond de modifications IA atteint (${modifications})`);
  }

  return out;
}

/**
 * Les messages de l'utilisateur, dans l'ordre chronologique : prêts à
 * rejouer en navigation privée pour reproduire son cas.
 */
export function buildScenario(entries: AdminTimelineEntry[]): string {
  return entries
    .filter((e) => e.source === "ai" && e.kind === "user")
    .slice()
    .sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime())
    .map((e, i) => `${i + 1}. ${str(e.detail?.content) ?? e.summary}`)
    .join("\n");
}
